// Métricas de atendimento (/attendance-insights): custo, economia, resolução, motivos, clientes e faturamento.
// Só admin; super e revendedor escolhem a empresa. Spec: docs/superpowers/specs/2026-11-04-metricas-atendimento-design.md
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
} from "@material-ui/core";
import * as XLSX from "xlsx";
import moment from "moment";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import { EmptyState, MainContainer, PageHeader, Section, StatCard } from "../../components/ui";
import { getTokens } from "../../styles/tokens";
import { canManageTenants } from "../../utils/tenant";
import { brl, decimal, duration, handledByLabel, percent, topicLabel, trend } from "./format";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    filters: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 16 },
    field: { minWidth: 150 },
    cards: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12, marginBottom: 16 },
    stack: { display: "grid", gap: 16 },
    twoCols: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 16 },
    tabs: { marginBottom: 16, borderBottom: `1px solid ${t.line}` },
    loading: { display: "flex", justifyContent: "center", padding: 48 },
    note: { fontSize: 13, lineHeight: 1.5, color: t.textSecondary, margin: "0 0 12px" },
    formula: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      gap: 12,
      marginBottom: 12,
    },
    formulaLabel: { fontSize: 12, color: t.textSecondary },
    formulaValue: { fontSize: 20, fontWeight: 600, color: t.textPrimary, fontVariantNumeric: "tabular-nums" },
    barRow: {
      display: "grid",
      gridTemplateColumns: "minmax(110px, 200px) 1fr minmax(46px, auto)",
      alignItems: "center",
      gap: 10,
      padding: "5px 0",
      fontSize: 13,
      color: t.textPrimary,
      borderRadius: 4,
      "&:hover": { backgroundColor: t.rowHover },
    },
    barName: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    barTrack: { height: 10, position: "relative" },
    bar: { height: "100%", minWidth: 2, backgroundColor: t.brand, borderRadius: "0 4px 4px 0" },
    barValue: { textAlign: "right", fontVariantNumeric: "tabular-nums", color: t.textSecondary },
    tableWrap: { overflowX: "auto" },
    num: { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
    link: { cursor: "pointer" },
    settingsField: { marginBottom: 12 },
  };
});

const BASE_TABS = [
  { key: "summary", label: "Resumo" },
  { key: "costs", label: "Custos e economia" },
  { key: "service", label: "Atendimento" },
  { key: "topics", label: "Motivos" },
  { key: "customers", label: "Clientes" },
  { key: "revenue", label: "Faturamento" },
  { key: "network", label: "Rede", ispOnly: true },
  { key: "learning", label: "Aprendizado" },
];

const PRESETS = [
  { value: "7", label: "Últimos 7 dias" },
  { value: "30", label: "Últimos 30 dias" },
  { value: "90", label: "Últimos 90 dias" },
  { value: "custom", label: "Personalizado" },
];

const Bars = ({ rows, format = (v) => v }) => {
  const classes = useStyles();
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div>
      {rows.map((r) => (
        <Tooltip key={r.name} title={r.tip || `${r.name}: ${format(r.value)}`} placement="top" arrow>
          <div className={classes.barRow}>
            <span className={classes.barName}>{r.name}</span>
            <div className={classes.barTrack}>
              <div className={classes.bar} style={{ width: `${(r.value / max) * 100}%` }} />
            </div>
            <span className={classes.barValue}>{format(r.value)}</span>
          </div>
        </Tooltip>
      ))}
    </div>
  );
};

const DataTable = ({ columns, rows, onRowClick, empty = "Sem dados no período." }) => {
  const classes = useStyles();
  if (!rows.length) return <p className={classes.note}>{empty}</p>;
  return (
    <div className={classes.tableWrap}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.title} align={c.numeric ? "right" : "left"}>
                {c.title}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow
              key={row.id || row.key || i}
              hover={!!onRowClick}
              className={onRowClick ? classes.link : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((c) => (
                <TableCell key={c.title} align={c.numeric ? "right" : "left"} className={c.numeric ? classes.num : undefined}>
                  {c.render(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const dataUntilText = (until) =>
  until ? `Dados até ${moment(until).format("DD/MM/YYYY")}. A avaliação de uma conversa pode levar até 3 dias.` : "Ainda não há conversas avaliadas no período.";

const Summary = ({ data }) => {
  const classes = useStyles();
  const { current: c, previous: p, economy: e, rating, revenue: r } = data;
  const share = (n) => percent(n, c.total);
  return (
    <div className={classes.stack}>
      <p className={classes.note}>{dataUntilText(c.dataUntil)}</p>
      <div className={classes.cards}>
        <StatCard label="Atendimentos" value={c.total} trend={trend(c.total, p.total)} hint="Conversas com cliente" />
        <StatCard label="Resolvidos pela IA" value={c.byAi} hint={`${share(c.byAi)} dos atendimentos`} trend={trend(c.byAi, p.byAi)} />
        <StatCard label="Resolvidos pelo robô" value={c.byBot} hint={`${share(c.byBot)} dos atendimentos`} trend={trend(c.byBot, p.byBot)} />
        <StatCard label="Resolvidos pela equipe" value={c.byTeam} hint={`${share(c.byTeam)} dos atendimentos`} trend={trend(c.byTeam, p.byTeam)} />
        <StatCard label="Economia estimada" value={brl(e.netSaving)} hint={`Equivale a ${decimal(e.attendantsEquivalent)} atendente(s)`} />
        <StatCard label="Custo de IA" value={brl(data.aiCost)} trend={trend(data.aiCost, data.previousAiCost, true)} hint={data.ownKeyMeasuredSince ? `Chave própria desde ${moment(data.ownKeyMeasuredSince).format("DD/MM")}` : "Pela chave da plataforma"} />
        <StatCard label="Nota média" value={rating.average === null ? "—" : `${decimal(rating.average)} / 10`} hint={`${rating.count} avaliações`} />
        <StatCard label="Voltaram pelo mesmo motivo" value={c.recurred} hint={c.classified ? `${percent(c.recurred, c.classified)} das classificadas` : "Aguardando classificação"} />
        {data.businessKind === "hospedagem" ? (
          <StatCard label="Reservas pagas" value={brl(r.dealsClosedAmount)} hint={`${r.dealsClosed} do agente, desde 04/11/2026`} />
        ) : (
          <StatCard label="Pagas depois do aviso" value={brl(r.invoicesPaidAmount)} hint={`${r.invoicesPaid} de ${r.invoicesSent} faturas avisadas`} />
        )}
      </div>
      <p className={classes.note}>
        A economia é uma estimativa, com a conta aberta em Custos e economia. "Pagas depois do aviso" não prova que o aviso causou o pagamento. Os envios da régua não contam como atendimento. A comparação com o período anterior vale menos enquanto o histórico é curto.
      </p>
    </div>
  );
};

const Costs = ({ data }) => {
  const classes = useStyles();
  const { economy: e, ai } = data;
  return (
    <div className={classes.stack}>
      <Section title="Quanto a IA e o robô economizaram (estimativa)">
        <div className={classes.formula}>
          <div>
            <div className={classes.formulaLabel}>Resolvidos sem atendente</div>
            <div className={classes.formulaValue}>{e.resolvedWithoutHuman}</div>
          </div>
          <div>
            <div className={classes.formulaLabel}>× tempo por atendimento</div>
            <div className={classes.formulaValue}>{decimal(e.minutesPerTicket)} min</div>
          </div>
          <div>
            <div className={classes.formulaLabel}>× custo por hora</div>
            <div className={classes.formulaValue}>{brl(e.hourlyCost)}</div>
          </div>
          <div>
            <div className={classes.formulaLabel}>− custo de IA</div>
            <div className={classes.formulaValue}>{brl(e.aiCostBrl)}</div>
          </div>
          <div>
            <div className={classes.formulaLabel}>= economia líquida</div>
            <div className={classes.formulaValue}>{brl(e.netSaving)}</div>
          </div>
        </div>
        <p className={classes.note}>
          {decimal(e.hoursSaved)} horas de atendimento, equivalentes a {decimal(e.attendantsEquivalent)} atendente(s) de 176 h.{" "}
          {e.minutesSource === "measured"
            ? "O tempo é a média real da sua equipe no período."
            : "O tempo é um padrão de 6 min, porque há poucos atendimentos medidos da equipe."}{" "}
          {e.hourlyCostSource === "default" && "O custo por hora é um padrão de R$ 25,00: informe o seu em Parâmetros de custo."}{" "}
          Atendimentos que a IA começou e a equipe terminou não entram na conta.
        </p>
      </Section>
      <div className={classes.cards}>
        <StatCard label="Custo total no período" value={brl(data.total)} hint={data.perSession === null ? "" : `${brl(data.perSession)} por atendimento`} />
        <StatCard label="IA" value={brl(ai.total)} hint={data.aiPerResolved === null ? "Nenhum resolvido só pela IA" : `${brl(data.aiPerResolved)} por resolvido pela IA`} />
        <StatCard
          label={data.team === null ? "Equipe (estimado)" : "Equipe (informado)"}
          value={brl(data.team === null ? data.teamEstimated : data.team)}
          hint={`${decimal(data.teamHours)} h de conversa ativa`}
        />
        <StatCard label="Plataforma" value={data.platform === null ? "—" : brl(data.platform)} hint="Proporcional ao período" />
        <StatCard
          label="Mensagens Meta"
          value={data.metaCost === null ? "Não informado" : brl(data.metaCost)}
          hint={`${data.metaMessages} pela API oficial`}
        />
        <StatCard label="IA fora do horário comercial" value={data.aiOffHours} hint={`De ${data.aiSessions} conversas com IA`} />
      </div>
      <p className={classes.note}>Horário comercial aqui: segunda a sexta das 8h às 18h e sábado das 8h às 12h.</p>
      <Section title="Custo de IA por agente">
        <Bars rows={data.byAgent.map((a) => ({ name: a.agent, value: a.paid, tip: `${a.agent}: ${brl(a.paid)} em ${a.calls} chamadas` }))} format={brl} />
      </Section>
      <Section title="Custo de IA por uso">
        <DataTable
          columns={[
            { title: "Uso", render: (r) => sourceLabel(r.source) },
            { title: "Chave", render: (r) => (r.metered ? "Plataforma" : "Própria") },
            { title: "Chamadas", numeric: true, render: (r) => r.calls },
            { title: "Pago", numeric: true, render: (r) => brl(r.paid) },
          ]}
          rows={ai.bySource.map((r) => ({ ...r, key: `${r.source}-${r.metered}` }))}
        />
      </Section>
    </div>
  );
};

const SOURCES = {
  isp_ai: "Atendimento de IA (provedor)",
  hospedagem_ai: "Atendimento de IA (hospedagem)",
  ai_learning: "Aprendizado",
  ai_insights: "Classificação das métricas",
  transcribe_button: "Transcrição de áudio",
  media_read: "Leitura de foto e PDF",
  copilot: "Copiloto do atendente",
  talkai: "Prompt de IA",
};
const sourceLabel = (s) => SOURCES[s] || s;

const Service = ({ data }) => {
  const classes = useStyles();
  const f = data.funnel;
  return (
    <div className={classes.stack}>
      <Section title="Caminho do atendimento">
        <Bars
          rows={[
            { name: "Chegaram", value: f.arrived },
            { name: "Passaram pela IA", value: f.ai },
            { name: "IA transferiu", value: f.aiTransferred },
            { name: "Transferência evitável", value: f.avoidable },
            { name: "IA resolveu sozinha", value: f.aiResolved },
            { name: "Resolvidos no total", value: f.resolved },
            { name: "Cliente sumiu", value: f.vanished },
          ]}
        />
        <p className={classes.note}>"Transferência evitável" é a leitura da IA sobre a conversa: o assistente poderia ter feito sozinho.</p>
      </Section>
      <Section title="Tempos por quem atendeu">
        <DataTable
          columns={[
            { title: "Quem atendeu", render: (r) => handledByLabel(r.who) },
            { title: "Conversas", numeric: true, render: (r) => r.sessions },
            { title: "Resolvidas", numeric: true, render: (r) => percent(r.resolved, r.sessions) },
            { title: "1ª resposta", numeric: true, render: (r) => duration(r.firstResponseSec) },
            { title: "Espera por atendente", numeric: true, render: (r) => duration(r.humanWaitSec) },
            { title: "Tempo ativo", numeric: true, render: (r) => duration(r.activeSec) },
          ]}
          rows={data.groups.map((g) => ({ ...g, key: g.who }))}
        />
        <p className={classes.note}>Medianas. Tempo ativo soma só os períodos de troca de mensagens, sem a noite em que o ticket ficou aberto.</p>
      </Section>
      <Section title="Por atendente">
        <DataTable
          columns={[
            { title: "Atendente", render: (r) => r.name },
            { title: "Conversas", numeric: true, render: (r) => r.sessions },
            { title: "Resolvidas", numeric: true, render: (r) => percent(r.resolved, r.sessions) },
            { title: "Tempo ativo médio", numeric: true, render: (r) => duration(r.activeAvgSec) },
            { title: "Espera até assumir", numeric: true, render: (r) => duration(r.waitSec) },
            { title: "Cliente voltou", numeric: true, render: (r) => r.reopened + r.recurred },
            { title: "Nota", numeric: true, render: (r) => (r.rating === null ? "—" : decimal(r.rating)) },
          ]}
          rows={data.attendants}
        />
      </Section>
      {data.stopPoints.length > 0 && (
        <Section title="Onde os clientes desistem">
          <Bars rows={data.stopPoints.map((s) => ({ name: s.point, value: s.count }))} />
        </Section>
      )}
    </div>
  );
};

const Topics = ({ data }) => {
  const classes = useStyles();
  const { coverage: cov } = data;
  const aiBest = [...data.topics].filter((t) => t.aiResolved > 0).sort((a, b) => b.aiResolved - a.aiResolved).slice(0, 10);
  if (!data.topics.length) {
    return <EmptyState title="Motivos ainda em classificação" description={`A rotina da madrugada classifica as conversas. Até agora: ${cov.classified} de ${cov.total}.`} />;
  }
  return (
    <div className={classes.stack}>
      <p className={classes.note}>
        Classificadas pela IA: {cov.classified} de {cov.total} conversas do período. {cov.noSubject} sem assunto (só cumprimento ou "ok") ficam fora do ranking; conversas internas não contam como atendimento.
      </p>
      <div className={classes.twoCols}>
        <Section title="Por que os clientes procuram">
          <Bars rows={data.topics.slice(0, 12).map((t) => ({ name: topicLabel(t.topic), value: t.sessions }))} />
        </Section>
        <Section title="O que a IA mais resolveu">
          {aiBest.length ? (
            <Bars rows={aiBest.map((t) => ({ name: topicLabel(t.topic), value: t.aiResolved, tip: `${topicLabel(t.topic)}: ${t.aiResolved} de ${t.aiTouched} conversas com IA` }))} />
          ) : (
            <p className={classes.note}>Nenhuma conversa resolvida pela IA no período.</p>
          )}
        </Section>
      </div>
      <Section title="Detalhe por motivo">
        <DataTable
          columns={[
            { title: "Motivo", render: (r) => topicLabel(r.topic) },
            { title: "Conversas", numeric: true, render: (r) => r.sessions },
            { title: "Período anterior", numeric: true, render: (r) => r.previous },
            { title: "Com IA", numeric: true, render: (r) => r.aiTouched },
            { title: "IA resolveu", numeric: true, render: (r) => r.aiResolved },
            { title: "Resolvidas", numeric: true, render: (r) => percent(r.resolved, r.sessions) },
            { title: "Voltaram", numeric: true, render: (r) => r.recurred },
            { title: "Insatisfeitos", numeric: true, render: (r) => r.negative },
          ]}
          rows={data.topics.map((t) => ({ ...t, key: t.topic }))}
        />
      </Section>
      <Section title="Perguntas que a IA não soube responder">
        <DataTable
          columns={[
            { title: "Pergunta", render: (r) => r.question },
            { title: "Vezes", numeric: true, render: (r) => r.count },
          ]}
          rows={data.unanswered.map((u) => ({ ...u, key: u.question }))}
          empty="Nenhuma no período."
        />
      </Section>
    </div>
  );
};

const Customers = ({ data, search, onSearch, onOpen }) => {
  const classes = useStyles();
  const { totals: t } = data;
  return (
    <div className={classes.stack}>
      <div className={classes.cards}>
        <StatCard label="Clientes atendidos" value={t.contacts} />
        <StatCard label="Voltaram pelo mesmo motivo" value={t.recurring} hint={percent(t.recurring, t.contacts)} />
        <StatCard label="Ameaçaram cancelar" value={t.threatening} hint={percent(t.threatening, t.contacts)} />
      </div>
      <Section
        title="Clientes que mais precisam de atenção"
        actions={<TextField size="small" variant="outlined" placeholder="Nome ou número" value={search} onChange={(e) => onSearch(e.target.value)} />}
      >
        <p className={classes.note}>
          O índice soma ameaças de cancelar, retornos pelo mesmo motivo, reclamações e insatisfação, de 0 a 100. Serve para ordenar a lista, não é previsão de cancelamento.
        </p>
        <DataTable
          onRowClick={onOpen}
          columns={[
            { title: "Cliente", render: (r) => r.name },
            { title: "Número", render: (r) => r.number },
            { title: "Índice", numeric: true, render: (r) => r.index },
            { title: "Conversas", numeric: true, render: (r) => r.sessions },
            { title: "Reclamações", numeric: true, render: (r) => r.complaints },
            { title: "Voltou", numeric: true, render: (r) => r.recurred },
            { title: "Ameaçou cancelar", numeric: true, render: (r) => r.threats },
            { title: "Motivo mais comum", render: (r) => topicLabel(r.topTopic) },
            { title: "Último contato", render: (r) => moment(r.lastAt).format("DD/MM/YYYY") },
          ]}
          rows={data.customers}
        />
      </Section>
    </div>
  );
};

const CustomerDialog = ({ customer, data, onClose }) => (
  <Dialog open={!!customer} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>{customer?.name}</DialogTitle>
    <DialogContent>
      {!data ? (
        <CircularProgress size={24} />
      ) : (
        <DataTable
          columns={[
            { title: "Data", render: (r) => moment(r.closedAt).format("DD/MM/YYYY HH:mm") },
            { title: "Ticket", render: (r) => r.ticketId },
            { title: "Motivo", render: (r) => topicLabel(r.topic) },
            { title: "Quem atendeu", render: (r) => handledByLabel(r.handledBy) },
            { title: "Resolvido", render: (r) => (r.success ? "Sim" : "Não") },
            { title: "Voltou", render: (r) => (r.recurredAt ? moment(r.recurredAt).format("DD/MM") : "—") },
          ]}
          rows={data.sessions.map((s, i) => ({ ...s, key: `${s.ticketId}-${i}` }))}
        />
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Fechar</Button>
    </DialogActions>
  </Dialog>
);

const DEAL_LABELS = { proposta: "Proposta", pre_reserva: "Pré-reserva" };

const Revenue = ({ data, businessKind }) => {
  const classes = useStyles();
  const { billing: b, salesFunnel: s } = data;
  const hasBilling = b.sent > 0 || b.unnotified > 0;
  const hasSales = s.quoted > 0 || data.deals.length > 0 || businessKind === "hospedagem";
  return (
    <div className={classes.stack}>
      {hasBilling && (
        <Section title="Cobrança">
          <div className={classes.cards}>
            <StatCard label="Faturas avisadas" value={b.sent} hint={brl(b.sentAmount)} />
            <StatCard label="Pagas depois do aviso" value={b.paid} hint={`${brl(b.paidAmount)} · ${percent(b.paid, b.sent)}`} />
            <StatCard label="Avisadas e ainda em aberto" value={b.unpaid} hint={brl(b.unpaidAmount)} />
            <StatCard label="Sem aviso" value={b.unnotified} hint={brl(b.unnotifiedAmount)} />
            <StatCard label="Dias até pagar" value={b.daysToPay === null ? "—" : decimal(b.daysToPay)} hint="Mediana desde o 1º aviso" />
          </div>
          <p className={classes.note}>
            {b.lastCheck ? `Última conferência no ERP: ${moment(b.lastCheck).format("DD/MM/YYYY HH:mm")}.` : "A conferência no ERP roda de madrugada."}{" "}
            "Depois do aviso" não prova que o aviso causou o pagamento. Considera os avisos da régua e das campanhas.
          </p>
          {data.failures.length > 0 && (
            <Bars rows={data.failures.map((f) => ({ name: f.motive === "skipped_no_phone" ? "Sem telefone no ERP" : f.motive, value: f.count }))} />
          )}
        </Section>
      )}
      {hasSales && (
        <Section title="Vendas">
          <Bars
            rows={[
              { name: "Chegaram", value: s.arrived },
              { name: "Receberam cotação", value: s.quoted },
              { name: "Receberam proposta", value: s.proposed },
              { name: "Fecharam", value: s.closed },
            ]}
          />
          <DataTable
            columns={[
              { title: "Tipo", render: (r) => DEAL_LABELS[r.kind] || r.kind },
              { title: "Situação", render: (r) => r.status },
              { title: "Quantidade", numeric: true, render: (r) => r.count },
              { title: "Valor", numeric: true, render: (r) => brl(r.amount) },
            ]}
            rows={data.deals.map((d) => ({ ...d, key: `${d.kind}-${d.status}` }))}
            empty="As propostas passam a ser registradas a partir de agora."
          />
        </Section>
      )}
      {!hasBilling && !hasSales && <EmptyState title="Sem faturamento no período" description="Não há faturas avisadas nem vendas registradas." />}
    </div>
  );
};

const Network = ({ data, by, onBy }) => {
  const classes = useStyles();
  return (
    <Section
      title="Reclamações técnicas por local"
      actions={
        <TextField select size="small" variant="outlined" value={by} onChange={(e) => onBy(e.target.value)} className={classes.field}>
          <MenuItem value="city">Cidade</MenuItem>
          <MenuItem value="olt">OLT</MenuItem>
          <MenuItem value="pon">PON</MenuItem>
        </TextField>
      }
    >
      <DataTable
        columns={[
          { title: "Local", render: (r) => r.place },
          { title: "Conversas", numeric: true, render: (r) => r.sessions },
          { title: "Clientes", numeric: true, render: (r) => r.customers },
          { title: "Contratos no local", numeric: true, render: (r) => r.contracts },
          { title: "% dos contratos", numeric: true, render: (r) => (r.share === null ? "—" : `${String(r.share).replace(".", ",")}%`) },
        ]}
        rows={data.places.map((p) => ({ ...p, key: p.place }))}
        empty="Sem dados. Depende da classificação e do vínculo do contato com o contrato."
      />
    </Section>
  );
};

const Learning = ({ data }) => {
  const classes = useStyles();
  const rateText = (s) => (s.rate === null ? "—" : `${String(s.rate).replace(".", ",")}% (${s.sessions})`);
  return (
    <Section title="Antes e depois de cada lição">
      <p className={classes.note}>
        Taxa de sucesso das conversas nos 14 dias antes e depois de a lição nascer, com a quantidade entre parênteses. Outros fatores também mudam nesse período, então isso não prova causa.
      </p>
      <DataTable
        columns={[
          { title: "Lição", render: (r) => r.text },
          { title: "Criada em", render: (r) => moment(r.createdAt).format("DD/MM/YYYY") },
          { title: "Antes", numeric: true, render: (r) => rateText(r.before) },
          { title: "Depois", numeric: true, render: (r) => rateText(r.after) },
        ]}
        rows={data.lessons}
        empty="Nenhuma lição ativa nos últimos 120 dias."
      />
    </Section>
  );
};

const SETTINGS_FIELDS = [
  { key: "attendantHourlyCost", label: "Custo por hora de um atendente (R$)", help: "Usado na economia. Sem valor, usa R$ 25,00." },
  { key: "attendantsMonthlyCost", label: "Custo mensal da equipe (R$)", help: "Salários e encargos. Sem valor, a equipe é estimada pelo tempo ativo." },
  { key: "platformMonthlyCost", label: "Mensalidade da plataforma (R$)", help: "Sem valor, usa o preço do plano." },
  { key: "metaMessageCost", label: "Custo médio por mensagem da API oficial (R$)", help: "Só para quem usa a API oficial da Meta." },
];

const SettingsDialog = ({ open, companyId, onClose, onSaved }) => {
  const classes = useStyles();
  const [values, setValues] = useState({});
  useEffect(() => {
    if (!open) return;
    api
      .get("/attendance-insights/settings", { params: { companyId } })
      .then(({ data }) => {
        const next = {};
        SETTINGS_FIELDS.forEach((f) => {
          next[f.key] = data[f.key] === null ? "" : String(data[f.key]);
        });
        setValues(next);
      })
      .catch(toastError);
  }, [open, companyId]);
  const save = async () => {
    try {
      await api.put("/attendance-insights/settings", values, { params: { companyId } });
      toast.success("Parâmetros salvos.");
      onSaved();
    } catch (err) {
      toastError(err);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Parâmetros de custo</DialogTitle>
      <DialogContent>
        {SETTINGS_FIELDS.map((f) => (
          <TextField
            key={f.key}
            className={classes.settingsField}
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            inputProps={{ min: 0, step: "0.01" }}
            label={f.label}
            helperText={f.help}
            value={values[f.key] ?? ""}
            onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button color="primary" variant="contained" onClick={save}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Linhas de cada aba para o Excel: o que a tela mostra, em colunas simples. */
const exportRows = (tab, d) => {
  switch (tab) {
    case "summary":
      return [{
        Atendimentos: d.current.total,
        "Resolvidos pela IA": d.current.byAi,
        "Resolvidos pelo robô": d.current.byBot,
        "Resolvidos pela equipe": d.current.byTeam,
        "Economia estimada": d.economy.netSaving,
        "Custo de IA": d.aiCost,
        "Nota média": d.rating.average,
      }];
    case "costs":
      return d.ai.bySource.map((r) => ({ Uso: sourceLabel(r.source), Chave: r.metered ? "Plataforma" : "Própria", Chamadas: r.calls, Custo: r.cost, Pago: r.paid }));
    case "service":
      return d.attendants.map((a) => ({ Atendente: a.name, Conversas: a.sessions, Resolvidas: a.resolved, "Tempo ativo médio (s)": a.activeAvgSec, Nota: a.rating }));
    case "topics":
      return d.topics.map((t) => ({ Motivo: topicLabel(t.topic), Conversas: t.sessions, Anterior: t.previous, "Com IA": t.aiTouched, "IA resolveu": t.aiResolved, Resolvidas: t.resolved, Voltaram: t.recurred, Insatisfeitos: t.negative }));
    case "customers":
      return d.customers.map((c) => ({ Cliente: c.name, Numero: c.number, Indice: c.index, Conversas: c.sessions, Reclamacoes: c.complaints, Voltou: c.recurred, "Ameaçou cancelar": c.threats, Motivo: topicLabel(c.topTopic) }));
    case "revenue":
      return d.deals.length
        ? d.deals.map((x) => ({ Tipo: x.kind, Situacao: x.status, Quantidade: x.count, Valor: x.amount }))
        : [{ Avisadas: d.billing.sent, "Valor avisado": d.billing.sentAmount, Pagas: d.billing.paid, "Valor pago": d.billing.paidAmount, "Sem aviso": d.billing.unnotified, "Valor sem aviso": d.billing.unnotifiedAmount }];
    case "network":
      return d.places.map((p) => ({ Local: p.place, Conversas: p.sessions, Clientes: p.customers, Contratos: p.contracts }));
    case "learning":
      return d.lessons.map((l) => ({ Licao: l.text, Criada: moment(l.createdAt).format("DD/MM/YYYY"), "Antes %": l.before.rate, "Depois %": l.after.rate }));
    default:
      return [];
  }
};

const AttendanceInsights = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const canPickCompany = !!user?.super || canManageTenants(user);
  const [tab, setTab] = useState("summary");
  const [preset, setPreset] = useState("30");
  const [from, setFrom] = useState(moment().subtract(29, "days").format("YYYY-MM-DD"));
  const [to, setTo] = useState(moment().format("YYYY-MM-DD"));
  const [companyId, setCompanyId] = useState("");
  const [companies, setCompanies] = useState([]);
  const [businessKind, setBusinessKind] = useState(null);
  // os dados ficam presos à aba que os pediu: ao trocar de aba, a tela nova nunca desenha os dados da anterior
  const [loaded, setLoaded] = useState(null);
  const data = loaded && loaded.tab === tab ? loaded.body : null;
  const lastRequest = useRef(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [by, setBy] = useState("city");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [customerData, setCustomerData] = useState(null);

  useEffect(() => {
    if (!canPickCompany) return;
    api.get("/companies/list").then(({ data: list }) => setCompanies(list || [])).catch(() => setCompanies([]));
  }, [canPickCompany]);

  const params = useMemo(() => {
    const p = {};
    if (companyId) p.companyId = companyId;
    if (preset === "custom") {
      p.from = from;
      p.to = to;
    } else {
      p.from = moment().subtract(Number(preset) - 1, "days").format("YYYY-MM-DD");
      p.to = moment().format("YYYY-MM-DD");
    }
    return p;
  }, [companyId, preset, from, to]);

  const load = useCallback(async () => {
    lastRequest.current += 1;
    const request = lastRequest.current;
    setLoading(true);
    setLoaded(null);
    try {
      const extra = tab === "customers" ? { search } : tab === "network" ? { by } : {};
      const { data: body } = await api.get(`/attendance-insights/${tab}`, { params: { ...params, ...extra } });
      // resposta de um pedido antigo (troca rápida de aba ou empresa) é descartada
      if (request !== lastRequest.current) return;
      setLoaded({ tab, body });
      if (body.businessKind) setBusinessKind(body.businessKind);
    } catch (err) {
      if (request === lastRequest.current) toastError(err);
    } finally {
      if (request === lastRequest.current) setLoading(false);
    }
  }, [tab, params, search, by]);

  useEffect(() => {
    const timer = setTimeout(load, tab === "customers" ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, tab]);

  // o tipo de negócio vem do resumo e decide se a aba Rede existe, em qualquer aba em que a página abra
  useEffect(() => {
    api
      .get("/attendance-insights/summary", { params })
      .then(({ data: body }) => setBusinessKind(body.businessKind))
      .catch(() => undefined);
  }, [params]);

  const openCustomer = async (row) => {
    setCustomer(row);
    setCustomerData(null);
    try {
      const { data: body } = await api.get(`/attendance-insights/customers/${row.id}`, { params: companyId ? { companyId } : {} });
      setCustomerData(body);
    } catch (err) {
      toastError(err);
    }
  };

  const exportExcel = () => {
    const rows = data ? exportRows(tab, data) : [];
    if (!rows.length) {
      toast.info("Não há dados para exportar.");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Metricas");
    XLSX.writeFile(wb, `metricas-${tab}-${params.from}-a-${params.to}.xlsx`);
  };

  if (user?.profile !== "admin" && !user?.super) return <ForbiddenPage />;
  const tabs = BASE_TABS.filter((t) => !t.ispOnly || businessKind === "isp");

  return (
    <MainContainer>
      <PageHeader
        title="Métricas de atendimento"
        subtitle="Custos, economia, resolução, recorrência e faturamento"
        actions={[
          <Button key="settings" variant="outlined" onClick={() => setSettingsOpen(true)}>
            Parâmetros de custo
          </Button>,
          <Button key="excel" variant="outlined" onClick={exportExcel}>
            Exportar Excel
          </Button>,
        ]}
      />
      <div className={classes.filters}>
        {canPickCompany && (
          <TextField select size="small" variant="outlined" label="Empresa" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className={classes.field} style={{ minWidth: 240 }} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
            <MenuItem value="">Minha empresa</MenuItem>
            {companies.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField select size="small" variant="outlined" label="Período" value={preset} onChange={(e) => setPreset(e.target.value)} className={classes.field}>
          {PRESETS.map((p) => (
            <MenuItem key={p.value} value={p.value}>
              {p.label}
            </MenuItem>
          ))}
        </TextField>
        {preset === "custom" && (
          <>
            <TextField type="date" size="small" variant="outlined" label="De" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
            <TextField type="date" size="small" variant="outlined" label="Até" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
          </>
        )}
      </div>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" indicatorColor="primary" textColor="primary" className={classes.tabs}>
        {tabs.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} />
        ))}
      </Tabs>
      {loading && (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      )}
      {!loading && data && tab === "summary" && <Summary data={data} />}
      {!loading && data && tab === "costs" && <Costs data={data} />}
      {!loading && data && tab === "service" && <Service data={data} />}
      {!loading && data && tab === "topics" && <Topics data={data} />}
      {!loading && data && tab === "customers" && <Customers data={data} search={search} onSearch={setSearch} onOpen={openCustomer} />}
      {!loading && data && tab === "revenue" && <Revenue data={data} businessKind={businessKind} />}
      {!loading && data && tab === "network" && <Network data={data} by={by} onBy={setBy} />}
      {!loading && data && tab === "learning" && <Learning data={data} />}
      <SettingsDialog
        open={settingsOpen}
        companyId={companyId || undefined}
        onClose={() => setSettingsOpen(false)}
        onSaved={() => {
          setSettingsOpen(false);
          load();
        }}
      />
      <CustomerDialog customer={customer} data={customerData} onClose={() => setCustomer(null)} />
    </MainContainer>
  );
};

export default AttendanceInsights;
